<script setup>
import moment from "moment-timezone";
moment.locale("id");
</script>

<template>
  <DefaultLayout class="!bg-slate-50">
    <v-row class="mx-3 my-0 font-inter" id="dashboard">
      <v-col cols="12">
        <div class="flex justify-between items-center">
          <div>
            <div class="flex justify-start items-center gap-1">
              <h5
                class="text-[10px] text-black text-nowrap cursor-pointer font-semibold"
                @click="$router.push('/dashboard-esg')"
              >
                Home
              </h5>
              <img
                src="../../assets/icon/mdi-chvron-right.svg"
                alt="icon"
                class="w-4 h-auto"
              />
              <span class="text-[10px] text-black font-semibold">
                ESG Dashboard
              </span>
              <img
                src="../../assets/icon/mdi-chvron-right.svg"
                alt="icon"
                class="w-4 h-auto"
              />
              <h2 class="text-[10px] text-red-600 text-nowrap font-semibold">
                Bahan Bakar, Energi & Emisi
              </h2>
            </div>
            <h2 class="font-bold text-xl text-[#232323]">ESG Dashboard</h2>
            <h5 class="font-normal italic text-gray-500 text-xs">
              Last updated on
              {{
                lastUpdate
                  ? moment(lastUpdate)
                      .tz("Asia/Singapore")
                      .format("DD MMM YYYY [-] HH.mm [(GMT +8)]")
                  : "-"
              }}
            </h5>
          </div>
          <div class="flex gap-2">
            <v-tooltip
              :text="years.find((item) => item.value === year)?.title"
              location="bottom"
            >
              <template v-slot:activator="{ props }">
                <v-autocomplete
                  v-bind="props"
                  variant="outlined"
                  color="#475569"
                  :items="years"
                  v-model="year"
                  item-title="title"
                  item-value="value"
                  hide-details
                  density="compact"
                  placeholder="Filter berdasarkan tahun"
                  @update:model-value="loadPage"
                  class="w-32"
                  @input="getYear($event.target.value)"
                  v-if="isAdmin"
                >
                </v-autocomplete>
              </template>
            </v-tooltip>
            <v-tooltip
              :text="
                contractors.find((item) => item.value === contractor)?.title
              "
              location="bottom"
            >
              <template v-slot:activator="{ props }">
                <v-autocomplete
                  v-bind="props"
                  variant="outlined"
                  color="#475569"
                  :items="contractors"
                  v-model="contractor"
                  item-title="title"
                  item-value="value"
                  hide-details
                  density="compact"
                  placeholder="Filter berdasarkan kontraktor"
                  @update:model-value="loadPage"
                  class="w-32"
                  v-if="isAdmin"
                >
                </v-autocomplete>
              </template>
            </v-tooltip>
            <v-tooltip
              :text="areas.find((item) => item.value === area)?.title"
              location="bottom"
            >
              <template v-slot:activator="{ props }">
                <v-autocomplete
                  v-bind="props"
                  variant="outlined"
                  color="#475569"
                  :items="areas"
                  v-model="area"
                  item-title="title"
                  item-value="value"
                  hide-details
                  density="compact"
                  placeholder="Filter berdasarkan area"
                  @update:model-value="loadPage"
                  class="w-32"
                >
                </v-autocomplete>
              </template>
            </v-tooltip>
            <v-btn
              variant="outlined"
              class="capitalize text-xs w-28 border-green-500"
              height="40"
              @click="downloadImage('dashboard')"
            >
              <template #prepend>
                <img
                  src="../../assets/icon/download-green.png"
                  alt="Download Icon"
                  class="h-4 w-auto"
                />
              </template>
              <span class="text-green-500 font-medium font-inter">
                Export
              </span>
            </v-btn>
          </div>
        </div>
        <div class="grid grid-cols-11 gap-2 mt-3">
          <div class="relative col-span-4">
            <v-tooltip text="Export" location="top">
              <template v-slot:activator="{ props }">
                <CardEnergyComponent
                  :data="ytd.energy"
                  id="ytd_energy_consume"
                />
                <img
                  v-bind="props"
                  src="../../assets/icon/mdi-download.svg"
                  alt="icon-download"
                  class="top-1 right-1 absolute cursor-pointer w-4 h-auto"
                  @click="downloadImage('ytd_energy_consume')"
                />
              </template>
            </v-tooltip>
          </div>
          <div class="relative col-span-3">
            <v-tooltip text="Export" location="top">
              <template v-slot:activator="{ props }">
                <CardFuelComponent :data="ytd.fuel" id="ytd_fuel_ratio" />
                <img
                  v-bind="props"
                  src="../../assets/icon/mdi-download.svg"
                  alt="icon-download"
                  class="top-1 right-1 absolute cursor-pointer w-4 h-auto"
                  @click="downloadImage('ytd_fuel_ratio')"
                />
              </template>
            </v-tooltip>
          </div>
          <div class="relative col-span-4">
            <v-tooltip text="Export" location="top">
              <template v-slot:activator="{ props }">
                <CardEmisionComponent :data="ytd.emission" id="ytd_emisi" />
                <img
                  v-bind="props"
                  src="../../assets/icon/mdi-download.svg"
                  alt="icon-download"
                  class="top-1 right-1 absolute cursor-pointer w-4 h-auto"
                  @click="downloadImage('ytd_emisi')"
                />
              </template>
            </v-tooltip>
          </div>
        </div>
        <div class="relative">
          <div id="dashboard_metrics" class="w-full h-32 lg:h-28">
            <div
              class="grid grid-cols-4 py-2 mt-4 bg-white rounded-md shadow-md"
            >
              <CardSummaryComponent
                v-for="(card, index) in summaryCards"
                :key="card.id"
                :item="card"
                :last="index === summaryCards.length - 1"
              />
            </div>
          </div>
          <v-tooltip text="Export" location="top">
            <template v-slot:activator="{ props }">
              <img
                @click="downloadImage('dashboard_metrics')"
                v-bind="props"
                src="../../assets/icon/mdi-download.svg"
                alt="icon-download"
                class="top-1 right-1 absolute cursor-pointer w-4 h-auto"
              />
            </template>
          </v-tooltip>
        </div>
        <div class="relative">
          <CardPowerUsedComponent
            :data="powerAvailable"
            id="daya_yang_digunakan"
          />
          <v-tooltip text="Export" location="top">
            <template v-slot:activator="{ props }">
              <img
                @click="downloadImage('daya_yang_digunakan')"
                v-bind="props"
                src="../../assets/icon/mdi-download.svg"
                alt="icon-download"
                class="top-1 right-1 absolute cursor-pointer w-4 h-auto"
              />
            </template>
          </v-tooltip>
        </div>
        <div
          class="grid grid-cols-2 gap-3 mt-4"
          v-if="version === TypeFormulaVersion.V1"
        >
          <CardChartComponent
            :height="200"
            :icon="chart.fuel.icon"
            :bg-color-icon="chart.fuel.color"
            :is-large="false"
            :title="chart.fuel.title"
            :type="chart.fuel.type"
            :data="chart.fuel.data"
            :legend="chart.fuel.legend"
            :loading="chart.fuel.loading"
            :id-ratio-legend="6"
            :get-chart="getChart"
          />
          <CardChartComponent
            :height="200"
            :icon="chart.energy.icon"
            :bg-color-icon="chart.energy.color"
            :is-large="false"
            :title="chart.energy.title"
            :type="chart.energy.type"
            :data="chart.energy.data"
            :legend="chart.energy.legend"
            :loading="chart.energy.loading"
            :id-ratio-legend="6"
            :get-chart="getChart"
          />
          <CardChartComponent
            :height="200"
            :icon="chart.emissionTon.icon"
            :bg-color-icon="chart.emissionTon.color"
            :is-large="false"
            :title="chart.emissionTon.title"
            :type="chart.emissionTon.type"
            :data="chart.emissionTon.data"
            :legend="chart.emissionTon.legend"
            :loading="chart.emissionTon.loading"
            :id-ratio-legend="6"
            :get-chart="getChart"
          />
          <CardChartComponent
            :height="200"
            :icon="chart.emissionTonCo2e.icon"
            :bg-color-icon="chart.emissionTonCo2e.color"
            :is-large="false"
            :title="chart.emissionTonCo2e.title"
            :type="chart.emissionTonCo2e.type"
            :data="chart.emissionTonCo2e.data"
            :legend="chart.emissionTonCo2e.legend"
            :loading="chart.emissionTonCo2e.loading"
            :id-ratio-legend="6"
            :get-chart="getChart"
          />
        </div>
        <div
          class="grid grid-cols-2 gap-3 mt-4"
          v-else-if="version === TypeFormulaVersion.V2"
        >
          <div class="relative">
            <CardChartV2Component
              :height="224"
              :icon="chart.fuel.icon"
              :bg-color-icon="chart.fuel.color"
              :is-large="false"
              :title="chart.fuel.title"
              :type="TypeChart.FUEL"
              :area="area === '' ? 'All' : area"
              :data="chart.fuel.data"
              :legend="chart.fuel.legend"
              :loading="chart.fuel.loading"
              :id-ratio-legend="7"
              :get-chart="getChart"
              id="konsumsi_bahan_bakar"
            />
            <v-tooltip text="Export" location="top">
              <template v-slot:activator="{ props }">
                <img
                  @click="downloadImage('konsumsi_bahan_bakar')"
                  v-bind="props"
                  src="../../assets/icon/mdi-download.svg"
                  alt="icon-download"
                  class="top-1 right-1 absolute cursor-pointer w-4 h-auto"
                />
              </template>
            </v-tooltip>
          </div>
          <div class="relative">
            <CardChartV2Component
              :height="224"
              :icon="chart.energy.icon"
              :bg-color-icon="chart.energy.color"
              :is-large="false"
              :title="chart.energy.title"
              :type="TypeChart.ENERGY"
              :area="area === '' ? 'All' : area"
              :data="chart.energy.data"
              :legend="chart.energy.legend"
              :loading="chart.energy.loading"
              :id-ratio-legend="7"
              :get-chart="getChart"
              id="konsumsi_energi"
            />
            <v-tooltip text="Export" location="top">
              <template v-slot:activator="{ props }">
                <img
                  @click="downloadImage('konsumsi_energi')"
                  v-bind="props"
                  src="../../assets/icon/mdi-download.svg"
                  alt="icon-download"
                  class="top-1 right-1 absolute cursor-pointer w-4 h-auto"
                />
              </template>
            </v-tooltip>
          </div>
          <div class="relative">
            <CardChartV2Component
              :height="224"
              :icon="chart.emissionTon.icon"
              :bg-color-icon="chart.emissionTon.color"
              :is-large="false"
              :title="chart.emissionTon.title"
              :type="TypeChart.EMISSION_TON"
              :area="area === '' ? 'All' : area"
              :data="chart.emissionTon.data"
              :legend="chart.emissionTon.legend"
              :loading="chart.emissionTon.loading"
              :id-ratio-legend="7"
              :get-chart="getChart"
              id="beban_emisi"
            />
            <v-tooltip text="Export" location="top">
              <template v-slot:activator="{ props }">
                <img
                  @click="downloadImage('beban_emisi')"
                  v-bind="props"
                  src="../../assets/icon/mdi-download.svg"
                  alt="icon-download"
                  class="top-1 right-1 absolute cursor-pointer w-4 h-auto"
                />
              </template>
            </v-tooltip>
          </div>
        </div>
      </v-col>
    </v-row>
  </DefaultLayout>
</template>

<script>
import DefaultLayout from "@/layouts/DefaultLayout.vue";
import GlobalDataMixin from "@/GlobalDataMixin";
import CardEnergyComponent from "./components/CardEnergyComponent.vue";
import CardFuelComponent from "./components/CardFuelComponent.vue";
import CardEmisionComponent from "./components/CardEmisionComponent.vue";
import CardSummaryComponent from "./components/CardSummaryComponent.vue";
import CardChartComponent from "./components/CardChartComponent.vue";
import util from "@/assets/js/util";
import axios from "axios";
import { APP_BACKEND_ESG } from "@/constants";
import { formatFloat, downloadElementAsPng } from "@/utilities/helpers";
import CardChartV2Component from "./components/CardChartV2Component.vue";
import { TypeChart, TypeFormulaVersion } from "@/utilities/enum";
import CardPowerUsedComponent from "./components/CardPowerUsedComponent.vue";

const base_url = APP_BACKEND_ESG;

export default {
  name: "DashboardPageEsg",
  mixins: [GlobalDataMixin],
  components: {
    DefaultLayout,
    CardEnergyComponent,
    CardFuelComponent,
    CardEmisionComponent,
    CardSummaryComponent,
    CardChartComponent,
    CardChartV2Component,
    CardPowerUsedComponent,
  },
  data() {
    return {
      isAdmin: false,
      workArea: null,
      kontraktorID: null,
      lastUpdate: null,
      years: [],
      contractors: [],
      areas: [
        { value: "", title: "All Area" },
        { value: "Hauling", title: "Hauling" },
        { value: "Mining", title: "Mining" },
        { value: "Project", title: "Project" },
        { value: "Port", title: "Port" },
        { value: "ROM", title: "ROM" },
        { value: "Others", title: "Others" },
      ],
      z: "",
      contractor: "",
      area: "",
      powerAvailable: [],
      ytd: {
        energy: {},
        fuel: {},
        emission: {},
      },
      summaryCards: [
        {
          id: 1,
          title: "Renewable<br/> Energy Used",
          value: "0",
          attribut: "Ton / Joule",
          color: "bg-emerald-600",
          icon: "hat.png",
        },
        {
          id: 2,
          title: "OB<br/> Production",
          value: "0",
          attribut: "MBCM",
          color: "bg-amber-600",
          icon: "excavator.png",
        },
        {
          id: 3,
          title: "Coal<br/> Production",
          value: "0",
          attribut: "MTON",
          color: "bg-gray-500",
          icon: "coal.png",
        },
        {
          id: 4,
          title: "Distance",
          value: "0",
          attribut: "KM",
          color: "bg-gray-400",
          icon: "distance.png",
        },
      ],
      chart: {
        fuel: {
          title: "Total Konsumsi Bahan Bakar",
          icon: "fuel-inactive.png",
          color: "bg-red-100",
          type: "FUEL",
          data: [],
          legend: [
            {
              id: 1,
              title: "Produksi (Ribu Ton)",
              show: true,
            },
            {
              id: 2,
              title: "Sumber Emisi Bergerak (Ribu Liter)",
              show: true,
            },
            {
              id: 3,
              title: "Sumber Emisi Tidak Bergerak (Ribu Liter)",
              show: true,
            },
            {
              id: 7,
              title: "Fuel Ratio (Liter Ton)",
              show: true,
            },
          ],
          loading: false,
        },
        energy: {
          title: "Total Konsumsi Energi (MJ)",
          icon: "elektrifikasi-inactive.png",
          color: "bg-green-100",
          type: "ENERGY",
          data: [],
          legend: [
            {
              id: 1,
              title: "Produksi (Ribu Ton)",
              show: true,
            },
            {
              id: 2,
              title: "Sumber Emisi Bergerak (Ribu Liter)",
              show: true,
            },
            {
              id: 3,
              title: "Sumber Emisi Tidak Bergerak (Ribu Liter)",
              show: true,
            },
            {
              id: 5,
              title: "Energi Terbarukan (x10^6 MJ)",
              show: true,
            },
            {
              id: 6,
              title: "Energi Elektrifikasi (x10^6 MJ)",
              show: true,
            },
            {
              id: 4,
              title: "Konsumsi Energi Fuel (x10^6 MJ)",
              show: true,
            },
            {
              id: 7,
              title: "Energi Ratio (MJ/Ton)",
              show: true,
            },
          ],
          loading: false,
        },
        emissionTon: {
          title: "Total Beban Emisi (Ton)",
          icon: "carbon-dioxide-orange.png",
          color: "bg-orange-100",
          type: "EMISSION TON",
          data: [],
          legend: [
            {
              id: 1,
              title: "Produksi (Ribu Ton)",
              show: true,
            },
            {
              id: 2,
              title: "Sumber Emisi Bergerak (Ribu Liter)",
              show: true,
            },
            {
              id: 3,
              title: "Sumber Emisi Tidak Bergerak (Ribu Liter)",
              show: true,
            },
            {
              id: 5,
              title: "Emisi Energi Terbarukan (Ribu TonCO2e)",
              show: true,
            },
            {
              id: 6,
              title: "Emisi Elektrifikasi (Ribu TonCO2e)",
              show: true,
            },
            {
              id: 8,
              title: "Emisi Fugitif (Ribu TonCO2e)",
              show: true,
            },
            {
              id: 4,
              title: "Emisi Fuel (Ribu TonCO2e)",
              show: true,
            },
            {
              id: 7,
              title: "Beban Emisi (Ribu Ton)",
              show: true,
            },
          ],
          loading: false,
        },
        emissionTonCo2e: {
          title: "Total Beban Emisi ((TonCO2-e))",
          icon: "carbon-dioxide-orange.png",
          color: "bg-orange-100",
          type: "EMISSION TON CO2-E",
          data: [],
          legend: [
            {
              id: 1,
              title: "Produksi (Ribu Ton)",
              show: true,
            },
            {
              id: 2,
              title: "Sumber Emisi Bergerak (RIbu Liter)",
              show: true,
            },
            {
              id: 3,
              title: "Sumber Emisi Tidak Bergerak (RIbu Liter)",
              show: true,
            },
            {
              id: 6,
              title: "Beban Emisi Ratio (x10-3 Ton/Ton)",
              show: true,
            },
            {
              id: 7,
              title: "Beban Emisi (Ribu Ton)",
              show: true,
            },
          ],
          loading: false,
        },
      },
      version: null,
    };
  },
  async mounted() {
    this.isAdmin =
      this.userData.role.includes("ADMIN") ||
      this.userData.role.includes("ENVIRONMENT") ||
      this.userData.role.includes("IMPROVEMENT");
    if (!this.isAdmin) {
      this.workArea = this.userData.workArea;
      this.kontraktorID = parseInt(this.userData.kontraktorID);
    }
    await this.getSetting();
    await this.getYear();
    if (!this.isAdmin) {
      const area = this.userData.workArea
        .replace("[", "")
        .replace("]", "")
        .split(",")
        .map((item) => item.trim());

      this.areas = [{ title: "All Area", value: "" }];

      this.areas.push(
        ...area.map((data) => ({
          title: data,
          value: data,
        })),
      );
    }
    this.loadPage();
    this.lastUpdated();
  },
  methods: {
    async loadPage() {
      await Promise.all([
        this.getGroupByInitials(),
        this.getYtd(),
        this.getCount(),
        this.getPowerAvailable(),
      ]);

      const charts = [
        this.getChart("FUEL", true),
        this.getChart("ENERGY", true),
        this.getChart("EMISSION TON", true),
      ];

      if (this.version === TypeFormulaVersion.V1) {
        charts.push(this.getChart("EMISSION TON CO2-E", true));
      }

      await Promise.all(charts);
    },
    downloadImage(element) {
      const area_name = this.areas.find(
        (item) => item.value === this.area,
      )?.title;
      const fileNameParts = [element, area_name];
      if (this.isAdmin) {
        const contractor_name = this.contractors.find(
          (item) => item.value === this.contractor,
        )?.title;
        const year_name = this.years.find(
          (item) => item.value === this.year,
        )?.title;
        fileNameParts.push(contractor_name, year_name);
      }
      downloadElementAsPng(element, { fileNameParts });
    },
    async getYtd() {
      try {
        let params = {
          source: "cms",
          id_year: this.year,
          area: this.area,
        };

        if (this.isAdmin) {
          params.id_contractor = this.contractor;
        } else {
          params.id_contractor = this.kontraktorID;
        }

        const response = await axios.get(`${base_url}/dashboard/ytd`, {
          params: params,
        });

        const result = response.data.data;
        if (result) {
          this.ytd.energy = result.consume_energy;
          this.ytd.fuel = result.fuel_ratio;
          this.ytd.emission = result.beban_emission;
        }
      } catch (err) {
        util.showError(err);
      }
    },
    async getCount() {
      try {
        let params = {
          source: "cms",
          id_year: this.year,
          area: this.area,
        };
        if (this.isAdmin) {
          params.id_contractor = this.contractor;
        } else {
          params.id_contractor = this.kontraktorID;
        }

        const response = await axios.get(`${base_url}/dashboard/count`, {
          params: params,
        });

        const result = response.data.data;

        if (result) {
          this.summaryCards.map((data) => {
            if (data.id === 1) {
              data.value = formatFloat(result.renewable_energy);
            }
            if (data.id === 2) {
              const value = result.production_ob;
              data.value = value === 0 ? "0" : formatFloat(value / 1000000);
            }
            if (data.id === 3) {
              const value = result.production_coal;
              data.value = value === 0 ? "0" : formatFloat(value / 1000000);
            }
            if (data.id === 4) {
              data.value = formatFloat(result.distance);
            }
            return data;
          });
        }
      } catch (err) {
        util.showError(err);
      }
    },
    async getChart(type, is_quarterly) {
      if (type === TypeChart.FUEL) this.chart.fuel.loading = true;
      if (type === TypeChart.ENERGY) this.chart.energy.loading = true;
      if (type === TypeChart.EMISSION_TON)
        this.chart.emissionTon.loading = true;
      if (type === "EMISSION TON CO2-E")
        this.chart.emissionTonCo2e.loading = true;
      try {
        let params = {
          source: "cms",
          type: type,
          is_quarterly: is_quarterly,
          id_year: this.year,
        };
        if (this.isAdmin) {
          params.id_contractor = this.contractor;
        } else {
          params.id_contractor = this.kontraktorID;
        }

        params.area = this.area;

        const response = await axios.get(`${base_url}/dashboard/chart`, {
          params: params,
        });

        const result = response.data.data;

        if (result) {
          if (type === TypeChart.FUEL) {
            const arrayData = result.map((data, index) => ({
              id: index,
              title: data.title,
              production: data.production,
              val_amber: data.emission_bergerak,
              val_red: data.emission_tidak_bergerak,
              dot_blue: data.fuel_ratio,
            }));

            const data = this.distributeRowHeightsAll(arrayData);

            this.chart.fuel.data = data;
          }
          if (type === TypeChart.ENERGY) {
            let arrayData = [];

            if (this.version === TypeFormulaVersion.V1) {
              arrayData = result.map((data, index) => ({
                id: index,
                title: data.title,
                production: data.production,
                val_amber: data.emission_bergerak,
                val_red: data.emission_tidak_bergerak,
                val_orange: data.consume_energy,
                val_emerald: data.energy_ratio,
                val_yellow: data.electrification,
                dot_blue: data.renewable_energy,
              }));
            } else if (this.version === TypeFormulaVersion.V2) {
              arrayData = result.map((data, index) => ({
                id: index,
                title: data.title,
                production: data.production,
                val_amber: data.emission_bergerak,
                val_red: data.emission_tidak_bergerak,
                val_orange: data.consume_energy,
                val_emerald: data.electrification,
                val_green: data.renewable_energy,
                dot_blue: data.energy_ratio,
              }));
            }

            this.chart.energy.data = this.distributeRowHeightsAll(arrayData);
          }
          if (type === TypeChart.EMISSION_TON) {
            let arrayData = [];
            if (this.version === TypeFormulaVersion.V1) {
              this.chart.emissionTon.data = result.map((data, index) => ({
                id: index,
                title: data.title,
                production: data.production,
                val_amber: data.emission_bergerak,
                val_red: data.emission_tidak_bergerak,
                val_yellow: data.beban_emission_ratio,
                dot_blue: data.beban_emission,
              }));
            } else if (this.version === TypeFormulaVersion.V2) {
              arrayData = result.map((data, index) => ({
                id: index,
                title: data.title,
                production: data.production,
                val_amber: data.emission_bergerak,
                val_red: data.emission_tidak_bergerak,
                val_orange: data.emission_fuel,
                val_green: data.emission_renewable,
                val_emerald: data.emission_electrification,
                val_lime: data.emission_fugitif,
                dot_blue: data.emission_ratio,
              }));
            }

            const data = this.distributeRowHeightsAll(arrayData);

            this.chart.emissionTon.data = data;
          }
          if (type === "EMISSION TON CO2-E") {
            this.chart.emissionTonCo2e.data = result.map((data, index) => ({
              id: index,
              title: data.title,
              production: data.production,
              val_amber: data.emission_bergerak,
              val_red: data.emission_tidak_bergerak,
              val_yellow: data.beban_emission_ratio,
              dot_blue: data.beban_emission,
            }));
          }
        }
      } catch (err) {
        util.showError(err);
      } finally {
        setTimeout(() => {
          if (type === "FUEL") this.chart.fuel.loading = false;
          if (type === "ENERGY") this.chart.energy.loading = false;
          if (type === "EMISSION TON") this.chart.emissionTon.loading = false;
          if (type === "EMISSION TON CO2-E")
            this.chart.emissionTonCo2e.loading = false;
        }, 500);
      }
    },
    async lastUpdated() {
      try {
        const res = await axios.get(`${base_url}/esg/last-updated`);

        this.lastUpdate = res.data.data?.updated_at;
      } catch (err) {
        util.showError(err);
      }
    },
    async getYear(keyword = null) {
      try {
        const year = await axios.get(`${base_url}/year`, {
          params: {
            name: keyword?.trim(),
            size: -1,
          },
        });
        this.years = year.data.data.map((item) => ({
          title: item.name,
          value: item.id,
        }));
        this.year = year.data.data.find(
          (item) => item.name == new Date().getFullYear(),
        )?.id;
      } catch (err) {
        util.showError(err);
      }
    },
    async getGroupByInitials() {
      try {
        let params = {
          source: "cms",
        };
        params.id_year = this.year;

        const response = await axios.get(
          `${base_url}/dashboard/group-by-initials`,
          {
            params: params,
          },
        );

        const result = response.data.data;

        this.contractors = [{ title: "All Kontraktor", value: "" }];
        this.contractors.push(
          ...result.map((data) => ({
            title: data.contractor_name,
            value: data.id_contractor,
          })),
        );
      } catch (err) {
        util.showError(err);
      }
    },
    getAdjustFactor(maxTotal) {
      const ranges = [
        { limit: 1e4, factor: 0.02 },
        { limit: 1e5, factor: 0.002 },
        { limit: 1e6, factor: 0.0002 },
        { limit: 1e7, factor: 0.00002 },
        { limit: 1e8, factor: 0.000002 },
        { limit: 1e9, factor: 0.0000002 },
        { limit: 1e10, factor: 0.00000002 },
        { limit: 1e11, factor: 0.000000002 },
        { limit: 1e12, factor: 0.0000000002 },
      ];

      for (const { limit, factor } of ranges) {
        if (maxTotal < limit) {
          return factor * maxTotal;
        }
      }

      // default kalau lebih besar dari semua limit
      return 0.00000000002 * maxTotal;
    },
    distributeRowHeightsAll(arrayData) {
      const MAX_HEIGHT = 224;
      const MIN_HEIGHT = 15;
      const EXCLUDE_KEYS = ["id", "type", "title", "dot_blue"];

      const totals = arrayData.map((row) => {
        const keys = Object.keys(row).filter((k) => !EXCLUDE_KEYS.includes(k));
        return keys.reduce((sum, k) => sum + row[k], 0);
      });

      const maxTotal = Math.max(...totals);
      const FACTOR = this.getAdjustFactor(maxTotal);

      const maxRatio = Math.max(...arrayData.map((d) => d.dot_blue));

      return arrayData.map((row, i) => {
        const keys = Object.keys(row).filter((k) => !EXCLUDE_KEYS.includes(k));
        const totalValue = totals[i];

        if (totalValue === 0) {
          const pxValues = {};
          keys.forEach((k) => (pxValues[`${k}_px`] = 0));
          return { ...row, ...pxValues };
        }

        const scaledMax = (totalValue / maxTotal) * MAX_HEIGHT;

        let pxValues = {};
        keys.forEach((k) => {
          let px = (row[k] / totalValue) * scaledMax;
          if (row[k] > 0 && px < MIN_HEIGHT) px = MIN_HEIGHT; // min jika > 0
          if (k === "fuel_useage") {
            px += FACTOR;
          }
          pxValues[`${k}_px`] = parseFloat(px.toFixed(4));
        });

        let totalPx = Object.values(pxValues).reduce((a, b) => a + b, 0);
        let excess = totalPx - MAX_HEIGHT;

        if (excess > 0) {
          let adjustableKeys = Object.keys(pxValues).filter(
            (k) => pxValues[k] > MIN_HEIGHT,
          );
          while (excess > 0 && adjustableKeys.length > 0) {
            let reducePerKey = excess / adjustableKeys.length;
            adjustableKeys.forEach((k) => {
              if (pxValues[k] > MIN_HEIGHT) {
                let newValue = pxValues[k] - reducePerKey;
                if (newValue < MIN_HEIGHT) {
                  excess -= pxValues[k] - MIN_HEIGHT;
                  pxValues[k] = MIN_HEIGHT;
                } else {
                  pxValues[k] = parseFloat(newValue.toFixed(4));
                  excess -= reducePerKey;
                }
              }
            });
            adjustableKeys = Object.keys(pxValues).filter(
              (k) => pxValues[k] > MIN_HEIGHT,
            );
          }
        }

        // posisi dot_blue
        let dotBluePos = 0;
        if (maxRatio > 0) {
          if (row.dot_blue === 0) {
            dotBluePos = 0;
          } else {
            dotBluePos = (row.dot_blue / maxRatio) * MAX_HEIGHT;

            const OFFSET_TOP_PERCENT = 0.3;
            const OFFSET_BOTTOM_PERCENT = 0.05;
            const OFFSET_TOP_PX = MAX_HEIGHT * OFFSET_TOP_PERCENT;
            const OFFSET_BOTTOM_PX = MAX_HEIGHT * OFFSET_BOTTOM_PERCENT;
            const TOLERANCE = 0.95;

            if (row.dot_blue === maxRatio) {
              dotBluePos = MAX_HEIGHT - OFFSET_TOP_PX;
            }

            if (dotBluePos < OFFSET_BOTTOM_PX) {
              dotBluePos = OFFSET_BOTTOM_PX;
            }

            if (row.dot_blue >= maxRatio * TOLERANCE) {
              dotBluePos = Math.min(dotBluePos, MAX_HEIGHT - OFFSET_TOP_PX - 5);
            }
          }
        }

        return { ...row, ...pxValues, dot_blue_px: dotBluePos };
      });
    },
    async getPowerAvailable() {
      try {
        let params = {
          source: "cms",
          id_year: this.year,
          area: this.area,
        };
        if (this.isAdmin) {
          params.id_contractor = this.contractor;
        } else {
          params.id_contractor = this.kontraktorID;
        }

        const res = await axios.get(
          `${base_url}/dashboard/compare-power-used`,
          {
            params: params,
          },
        );

        const response = res.data.data;

        const arrayData = Object.entries(response).map(([key, value]) => ({
          type: key,
          ...value,
        }));

        const result = this.distributeRowHeightsAll(arrayData);

        this.powerAvailable = result;
      } catch (err) {
        util.showError(err);
      }
    },
    async getSetting() {
      try {
        const response = await axios.get(`${base_url}/sys-setting`);

        this.version = response.data.data.formula_version;
      } catch (err) {
        util.showError(err);
      }
    },
  },
};
</script>
